import { useCallback, useEffect, useRef, useState } from 'react'
import { apiService } from '@/services/api'
import type { Mission, MissionSession } from '@/types'

/**
 * Estado das missões de uma mesa na tela de jogo. Carrega a lista por REST e
 * mantém a missão ativa em dia via os eventos do WebSocket (mission_changed /
 * mission_session_changed), reusando o hub `subscribe` do useGameWebSocket —
 * sem abrir um segundo listener no socket (invariante do GameSession).
 *
 * Toda mudança é opt-in: mesa sem missão devolve activeMission=null e a tela
 * de jogo se comporta como antes.
 */
export function useMissions(
  tableId: string | null,
  subscribe?: (fn: (data: any) => void) => () => void,
) {
  const [missions, setMissions] = useState<Mission[]>([])
  const [loaded, setLoaded] = useState(false)
  const tableRef = useRef(tableId)
  tableRef.current = tableId

  const refresh = useCallback(async () => {
    if (!tableRef.current) return
    try {
      const data = await apiService.getMissions(tableRef.current)
      setMissions(data)
    } catch (error) {
      console.error('[useMissions] erro ao carregar missões:', error)
    } finally {
      setLoaded(true)
    }
  }, [])

  useEffect(() => {
    if (!tableId) return
    setLoaded(false)
    refresh()
  }, [tableId, refresh])

  // Eventos de missão via o hub de frames já parseados do socket.
  useEffect(() => {
    if (!subscribe) return
    const unsub = subscribe((data: any) => {
      if (data?.type === 'mission_changed' || data?.type === 'mission_session_changed') {
        // Recarrega a lista — barata no volume do EduRPG e evita divergência
        // entre o payload parcial e a lista local (contagens, sessões).
        refresh()
      }
    })
    return unsub
  }, [subscribe, refresh])

  const activeMission = missions.find((m) => m.status === 'ativa') ?? null
  const openSession: MissionSession | null =
    activeMission?.sessions.find((s) => !s.ended_at) ?? null

  const startSession = useCallback(async () => {
    if (!tableRef.current || !activeMission) return
    await apiService.startMissionSession(tableRef.current, activeMission.id)
    await refresh()
  }, [activeMission, refresh])

  const endSession = useCallback(async () => {
    if (!tableRef.current || !activeMission || !openSession) return
    await apiService.endMissionSession(tableRef.current, activeMission.id, openSession.id)
    await refresh()
  }, [activeMission, openSession, refresh])

  return { missions, activeMission, openSession, loaded, refresh, startSession, endSession }
}
